import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X, Download, ChevronLeft, ChevronRight, ScrollText, RefreshCw } from 'lucide-react';
import { auditService } from '../../services/auditService';
import { useCatalog } from '../../lib/permissions';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';
import AuditLogTable, { formatTime, recordText } from './AuditLogTable';

const PAGE_SIZE = 25;

const useDebounced = (value, ms = 350) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return debounced;
};

const csvCell = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const inputClass = 'rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20';

const AuditLogsPage = () => {
    const { catalog } = useCatalog();
    const [meta, setMeta] = useState({ categories: [], eventTypes: [] });
    const [search, setSearch] = useState('');
    const [user, setUser] = useState('');
    const [category, setCategory] = useState('');
    const [eventType, setEventType] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [page, setPage] = useState(0);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const q = useDebounced(search);
    const principal = useDebounced(user);

    useEffect(() => {
        auditService.getEventTypes()
            .then((res) => setMeta(res?.data || { categories: [], eventTypes: [] }))
            .catch(() => {});
    }, []);

    useEffect(() => { setPage(0); }, [q, principal, category, eventType, from, to]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await auditService.search({
                q, principal, category, eventType, from, to, page, size: PAGE_SIZE,
            });
            setResult(res?.data || null);
        } catch (err) {
            setError(err.message || 'Failed to load audit logs');
        } finally {
            setLoading(false);
        }
    }, [q, principal, category, eventType, from, to, page]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const events = useMemo(
        () => meta.eventTypes.filter((e) => !category || e.category === category),
        [meta.eventTypes, category]
    );

    const filtered = search || user || category || eventType || from || to;

    const clear = () => {
        setSearch(''); setUser(''); setCategory(''); setEventType(''); setFrom(''); setTo('');
    };

    const rows = result?.content || [];
    const total = result?.totalElements || 0;
    const totalPages = result?.totalPages || 0;
    const start = total ? page * PAGE_SIZE + 1 : 0;
    const end = Math.min((page + 1) * PAGE_SIZE, total);

    const exportCsv = () => {
        const header = ['Time', 'User', 'Email', 'Action', 'Category', 'Record', 'Details', 'IP address'];
        const lines = rows.map((r) => {
            const t = formatTime(r.timestamp);
            return [
                `${t.date} ${t.time}`, r.actorName || '', r.principal || '', r.eventLabel, r.category,
                recordText(r), r.data ? JSON.stringify(r.data) : '', r.ipAddress || '',
            ].map(csvCell).join(',');
        });
        const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}-p${page + 1}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Audit Logs</h2>
                    <p className="text-gray-600 mt-1">Who did what, and when.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchLogs}
                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        onClick={exportCsv}
                        disabled={!rows.length}
                        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Download className="h-4 w-4" />
                        Export page
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {['', ...meta.categories].map((c) => (
                    <button
                        key={c || 'all'}
                        onClick={() => { setCategory(c); setEventType(''); }}
                        className={`rounded-full px-3 py-1.5 text-sm transition ${
                            category === c
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        {c || 'All activity'}
                    </button>
                ))}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1.3fr)_auto_auto_auto]">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search user or details…"
                            className={`${inputClass} w-full pl-9`}
                        />
                    </div>
                    <input
                        value={user}
                        onChange={(e) => setUser(e.target.value)}
                        placeholder="Exact user email"
                        className={`${inputClass} w-full`}
                    />
                    <select value={eventType} onChange={(e) => setEventType(e.target.value)} className={`${inputClass} w-full bg-white`}>
                        <option value="">{category ? `All ${category.toLowerCase()} events` : 'All events'}</option>
                        {events.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                    </select>
                    <label className="flex items-center gap-2 text-sm text-gray-500">
                        From
                        <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-500">
                        To
                        <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={inputClass} />
                    </label>
                    <button
                        onClick={clear}
                        disabled={!filtered}
                        className="flex items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <X className="h-4 w-4" />
                        Clear
                    </button>
                </div>
            </div>

            {error && <ErrorAlert error={error} onRetry={fetchLogs} />}

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                {loading && !result ? (
                    <LoadingSpinner message="Loading audit logs..." />
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center">
                        <ScrollText className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                        <h3 className="text-base font-medium text-gray-900">
                            {filtered ? 'Nothing matches these filters' : 'No activity recorded yet'}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {filtered ? 'Try a wider date range or clear the filters.' : 'Actions people take in Nexus will show up here.'}
                        </p>
                    </div>
                ) : (
                    <div className={loading ? 'opacity-60 transition' : 'transition'}>
                        <AuditLogTable rows={rows} catalog={catalog} />
                    </div>
                )}

                {total > 0 && (
                    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
                        <span>{start}–{end} of {total.toLocaleString()}</span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                                disabled={page === 0 || loading}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <ChevronLeft className="h-4 w-4" />Previous
                            </button>
                            <span className="px-1">Page {page + 1} of {Math.max(totalPages, 1)}</span>
                            <button
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page + 1 >= totalPages || loading}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Next<ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuditLogsPage;
